import { mdiAccountOutline, mdiCogOutline, mdiDnsOutline, mdiAbacus } from '@mdi/js'
import { ref, watch, getCurrentInstance } from '@vue/composition-api'
import { useActions } from 'vuex-composition-helpers'
import {
  areQueriesEqual,
  didSearchOrFilterValuesChange,
  parseFilterArrayFromQuery,
  parseSearchFromQuery,
  parseTableOptionsFromQuery,
  readListQueryFromSession,
  resetTablePageOnFilterChange,
  shouldResetTablePageAfterFetch,
  serializeFilterArrayToQuery,
  serializeSearchToQuery,
  serializeTableOptionsToQuery,
  shouldRestoreListStateFromUrlQuery,
  writeListQueryToSession,
} from '@/utils/listRouteState'

const LIST_ROUTE_NAME = 'apps-client-list'

export default function useClientList() {
  const vm = getCurrentInstance().proxy
  const clients = ref([])
  const choices = ref({})

  const statusesFilter = ref([])
  const cityFilter = ref([])
  const tagsFilter = ref([])

  const tableColumns = [
    { text: 'name', value: 'name', class: 'ps-7 text-uppercase' },
    { text: 'status', value: 'status', class: 'text-uppercase' },
    { text: 'tags', value: 'tags', class: 'text-uppercase', sortable: false },
    { text: 'projectsAmount', value: 'projectsAmount', align: 'center', class: 'text-uppercase' },
    { text: 'positionAmount', value: 'positionsAmount', align: 'center', class: 'text-uppercase' },
    { text: 'workload', value: 'projectsWorkload', align: 'center', class: 'text-uppercase' },
    { text: 'city', value: 'city', class: 'text-uppercase' },
    {
      text: 'actions',
      value: 'actions',
      align: 'center',
      sortable: false,
      class: 'text-uppercase',
    },
  ]

  const searchQuery = ref('')
  const totalClientListTable = ref(0)
  const loading = ref(false)
  const options = ref({
    sortDesc: [true],
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
  })
  const userTotalLocal = ref([])
  const overview = ref([])
  const isSyncingFromRoute = ref(false)

  const { getClients, getClientsFilterChoices, getClientsOverview } = useActions('staff', [
    'getClients',
    'getClientsFilterChoices',
    'getClientsOverview',
  ])

  const fetchClientsOverview = async () => {
    overview.value = await getClientsOverview()
  }

  const applyStateFromRoute = () => {
    isSyncingFromRoute.value = true

    let query = { ...vm.$route.query }
    if (shouldRestoreListStateFromUrlQuery(query)) {
      const stored = readListQueryFromSession(LIST_ROUTE_NAME)
      if (stored) {
        query = { ...stored }
      }
    }

    statusesFilter.value = parseFilterArrayFromQuery(query, 'status')
    cityFilter.value = parseFilterArrayFromQuery(query, 'city')
    tagsFilter.value = parseFilterArrayFromQuery(query, 'tags')
    searchQuery.value = parseSearchFromQuery(query)
    options.value = parseTableOptionsFromQuery(query, options.value)

    isSyncingFromRoute.value = false
    void syncStateToRoute()
  }

  const syncStateToRoute = async () => {
    if (isSyncingFromRoute.value) return

    const nextQuery = {
      ...serializeTableOptionsToQuery(options.value),
      ...serializeFilterArrayToQuery('status', statusesFilter.value),
      ...serializeFilterArrayToQuery('city', cityFilter.value),
      ...serializeFilterArrayToQuery('tags', tagsFilter.value),
      ...serializeSearchToQuery(searchQuery.value),
    }

    if (!areQueriesEqual(vm.$route.query, nextQuery)) {
      await vm.$router.replace({ query: nextQuery })
    }
    writeListQueryToSession(LIST_ROUTE_NAME, nextQuery)
  }

  // fetch data
  const fetchClients = async (isRetry = false) => {
    loading.value = true
    try {
      const { data, success } = await getClients({
        status: statusesFilter.value,
        city: cityFilter.value,
        tags: tagsFilter.value,
        search: searchQuery.value,
        page: options.value.page,
        pageSize: options.value.itemsPerPage,
        ordering: options.value.sortBy.map((el, index) => {
          const parsedEl = el
            .replaceAll('.', '__')
            .replace('projectsAmount', 'projects_amount')
            .replace('positionsAmount', 'positions_amount')
            .replace('projectsWorkload', 'projects_workload')

          return `${options.value.sortDesc[index] ? '-' : ''}${parsedEl}`
        }),
      })
      let total = 0
      if (success) {
        if (Object.hasOwnProperty.bind(data)('results') && Object.hasOwnProperty.bind(data)('count')) {
          clients.value = data.results
          totalClientListTable.value = data.count
          total = data.count
        } else {
          clients.value = data
          totalClientListTable.value = data.length
          total = data.length
        }
      } else {
        clients.value = []
        totalClientListTable.value = 0
      }
      if (
        !isRetry &&
        shouldResetTablePageAfterFetch({
          success,
          page: options.value.page,
          total,
          pageSize: options.value.itemsPerPage,
        })
      ) {
        options.value = { ...options.value, page: 1 }
        await syncStateToRoute()
        await fetchClients(true)
        return
      }
      choices.value = await getClientsFilterChoices({
        status: statusesFilter.value,
        city: cityFilter.value,
        tags: tagsFilter.value,
        search: searchQuery.value,
      })
    } finally {
      loading.value = false
    }
  }

  const timer = ref(null)
  const delaySearchHandler = async () => {
    clearTimeout(timer.value)
    timer.value = setTimeout(async () => {
      if (options.value.page !== 1) {
        options.value = { ...options.value, page: 1 }
      }
      await syncStateToRoute()
      await fetchClients()
    }, 1000)
  }

  applyStateFromRoute()

  watch(
    [statusesFilter, cityFilter, tagsFilter, options],
    async ([statuses, city, tags], oldValues) => {
      const filterChanged = didSearchOrFilterValuesChange({
        filterArrays: [statuses, city, tags],
        oldFilterArrays: oldValues ? oldValues.slice(0, 3) : [],
      })
      if (resetTablePageOnFilterChange(options, filterChanged)) {
        await syncStateToRoute()
        return
      }
      await syncStateToRoute()
      await fetchClients()
    },
  )

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveClientTotalIcon = (title) => {
    if (title === 'total') return { icon: mdiCogOutline, color: 'secondary' }
    if (title === 'position amount') return { icon: mdiDnsOutline, color: 'primary' }
    if (title === 'taken assignees') return { icon: mdiAccountOutline, color: 'success' }
    if (title === 'demand') return { icon: mdiAbacus, color: 'success' }

    return { icon: mdiAccountOutline, color: 'primary' }
  }

  return {
    fetchClientsOverview,
    fetchClients,
    resolveClientTotalIcon,
    delaySearchHandler,

    tableColumns,
    searchQuery,
    totalClientListTable,
    loading,
    options,
    userTotalLocal,
    overview,
    clients,
    choices,
    statusesFilter,
    cityFilter,
    tagsFilter,
  }
}
